/**
 * Pricing config utility — fetches live pricing from the DB and merges it
 * on top of JSON base data before calculations run.
 *
 * Usage:
 *   const config = await fetchPricingConfig('advantage')
 *   const materials = getMaterialsDataset('advantage', config)
 *   const parts = getPartsDataset('advantage', config)
 *   // formula constants: config.formula.galvRate, config.formula.overhead, etc.
 */

import axios from 'axios'

export const fetchPricingConfig = async (buildingType) => {
  // integrity-cattle uses the same config as integrity
  const configType = buildingType === 'integrity-cattle' ? 'integrity' : buildingType

  try {
    const res = await axios.get(`/api/pricing/config/${configType}`, { withCredentials: true })
    return res.data
  } catch {
    return null
  }
}

export const clearPricingConfigCache = () => {
  // no-op: cache removed, kept for call-site compatibility
}

/**
 * Merges DB material price overrides onto a JSON materials array.
 * Only pricePerPound and pricePerUnit are overridden — nothing else.
 */
export const applyMaterialOverrides = (materials, config) => {
  if (!config || !config.materials || !config.materials.length) return materials

  return materials.map(m => {
    const override = config.materials.find(o => o.materialId === m.id)
    if (!override) return m
    const newPricePerPound = override.pricePerPound ?? m.pricePerPound
    const newPricePerUnit = override.pricePerUnit ?? m.pricePerUnit
    return {
      ...m,
      pricePerPound: newPricePerPound,
      // If pricePerPound was overridden, clear pricePerUnit so getMaterialPricePerUnit recalculates it
      pricePerUnit: override.pricePerPound != null ? null : newPricePerUnit
    }
  })
}

/**
 * Merges DB part overrides (fastener/cover) onto a JSON parts array.
 * Only parts that already have hardcoded values are stored in the DB.
 */
export const applyPartOverrides = (parts, config) => {
  if (!config || !config.parts || !config.parts.length) return parts

  return parts.map(p => {
    const override = config.parts.find(o => o.partId === p.id)
    if (!override) return p
    return {
      ...p,
      fastener: override.fastener ?? p.fastener,
      cover: override.cover ?? p.cover
    }
  })
}

/**
 * Returns formula constants from DB config, falling back to hardcoded defaults
 * if config hasn't loaded yet.
 */
export const getFormulaConstants = (config) => ({
  galvRate: config?.formula?.galvRate ?? 0.34,
  overhead: config?.formula?.overhead ?? 0.55,
  margin: config?.formula?.margin ?? 0.70,
  markup: config?.formula?.markup ?? 1.05,
  fee105: config?.formula?.fee105 ?? 105
})
